import { IconButton } from '@material-ui/core';
import { Editor } from '@orientaction/components';
import { EditorCitation } from '@orientaction/components/Admin';
import clsx from 'clsx';
import { ReactComponent as Broder } from '../../../../../../Asset/svg/border_white.svg';
import { ReactComponent as Close } from '../../../../../../Asset/svg/close.svg';
import { ReactComponent as Enc } from '../../../../../../Asset/svg/enc.svg';
import { ReactComponent as Linked } from '../../../../../../Asset/svg/link.svg';
import VideoBlock from '../../../../shared/components/VideoImport/VideoBlock';
import RedesignImage from '../../../../shared/components/RedesignImage';
import { redesignTheme } from '../../../../theme';
import { UseEditEbookReturn } from '../hooks/useEditEbook';
import { EbookContentBlock } from '../types';
import { useBlockEditorStyles } from './BlockEditor.styles';
import QcmBlockEditor from './QcmBlockEditor';

type BlockEditorProps = {
  block: EbookContentBlock;
  index: number;
  allBlocks: EbookContentBlock[];
  edit: UseEditEbookReturn;
};

const BlockEditor = ({ block, index, allBlocks, edit }: BlockEditorProps) => {
  const classes = useBlockEditorStyles();

  const renderLines = (numLines: number) => (
    <div className={classes.linesContainer}>
      {Array.from({ length: numLines }, (_line, position) => (
        <div
          key={`line-${numLines}-${position + 1}`}
          className={clsx(classes.line, { [classes.lastLine]: position === numLines - 1 })}
        />
      ))}
    </div>
  );

  switch (block?.bloc_template) {
    case 1:
      if (block.contentForType === 'citation') {
        return (
          <EditorCitation
            key={index}
            defaultValue={block.citation}
            defaultAutor={block.author}
            handleChange={({ citation, autor }) => {
              edit.handleChangeInput(citation, index, 'citation');
              edit.handleChangeInput(autor, index, 'author');
            }}
            isEbook={true}
          />
        );
      }

      if (block.contentForType === 'encyclopedia') {
        return (
          <div className={classes.encyclopediaWrap}>
            <Enc className={classes.encyclopediaIcon} />
            <Editor
              isSmall={true}
              handleActiveStyles={edit.setStateEdition}
              handleFocused={() => undefined}
              styleEditor={block.styleEditor}
              showLinkPopupE={!!block.showLinkPopup}
              defaultValue={block.encyclopedia}
              key={index}
              isEbook={true}
              handleChange={content => edit.handleChangeInput(content, index, 'encyclopedia')}
              pulseMove={edit.pulseMove}
              placeholder="Cliquer pour ajouter une information"
              position={block.alignement}
              handlePosition={pos => {
                edit.setPosition(pos);
                edit.handleChangeInput(pos, index, 'alignement');
              }}
            />
          </div>
        );
      }

      return (
        <Editor
          isSmall={true}
          handleActiveStyles={edit.setStateEdition}
          handleFocused={() => undefined}
          styleEditor={block.styleEditor}
          showLinkPopupE={!!block.showLinkPopup}
          defaultValue={block.paragraphe}
          key={index}
          isEbook={true}
          handleChange={content => edit.handleChangeInput(content, index, 'paragraphe')}
          pulseMove={edit.pulseMove}
          position={block.alignement}
          handlePosition={pos => {
            edit.setPosition(pos);
            edit.handleChangeInput(pos, index, 'alignement');
          }}
          oneLine={true}
        />
      );

    case 2:
      return (
        <input
          id={`title-${index}`}
          className={classes.titleInput}
          style={{
            height: 30,
            fontFamily:
              block.title_type === 'subtitle'
                ? redesignTheme.fonts.opensansSemiBold
                : redesignTheme.fonts.opensansBold,
            fontSize: block.title_type === 'subtitle' ? 14 : 16,
          }}
          type="text"
          placeholder="Cliquer pour ajouter un titre"
          value={block.title || ''}
          onInput={event => edit.handleChangeInput(event.currentTarget.value, index, 'title')}
        />
      );

    case 3:
      return (
        <QcmBlockEditor
          block={block}
          blockIndex={index}
          onQuestionChange={value => edit.handleChangeInput(value, index, 'qcm_title')}
          onQuestionTypeChange={type => edit.handleChangeQuestionType(index, type)}
          onOptionChange={(responseIndex, value) =>
            edit.handleChangeOption(index, responseIndex, value)
          }
          onAddOption={isOther => edit.handleAddOption(index, isOther)}
          onDeleteOption={responseIndex => edit.handleDeleteOption(index, responseIndex)}
        />
      );

    case 4:
      return (
        <div className={classes.freeQuestionLines}>
          <textarea
            className={classes.editArea}
            placeholder="Cliquer pour rédiger une question"
            value={block.free_question_title || ''}
            onChange={event =>
              edit.handleChangeInput(event.target.value, index, 'free_question_title')
            }
            rows={2}
          />
          <label className={classes.lineControl} htmlFor={`num-lines-${index}`}>
            Nombre de lignes disponibles pour la réponse:
            <input
              id={`num-lines-${index}`}
              type="number"
              className={classes.numLinesInput}
              value={block.numLines || 7}
              min={1}
              onChange={event => edit.handleChangeNumLines(parseInt(event.target.value, 10), index)}
            />
          </label>
          {renderLines(block.numLines || 7)}
        </div>
      );

    case 5:
      return (
        <div className={classes.imageBlock}>
          <RedesignImage
            src={block.img?.url}
            alt=""
            loading="lazy"
            className={classes.imagePreview}
            draggable={false}
          />
          <div className={classes.imageActions}>
            <IconButton
              aria-label="Supprimer l'image"
              className={classes.imageActionBtn}
              style={{ background: '#E63C3C' }}
              onClick={() => {
                const updatedItems = [...allBlocks];
                updatedItems.splice(index, 1);
                edit.syncBlocks(updatedItems);
              }}>
              <Close className={classes.icon} />
            </IconButton>
            <IconButton
              aria-label="Modifier l'image"
              className={classes.imageActionBtn}
              style={{ background: '#000A8C' }}
              onClick={() => {
                const img = new Image();
                img.src = block.img?.url || '';
                img.onload = () => {
                  edit.setUrl(block.img?.url || '');
                  edit.setDataImg({
                    imgLink: block.imgLink || '',
                    checkedLink: !!block.checkedLink,
                  });
                  edit.setOpenEditImg(true);
                  edit.setIndexEditImg(index);
                };
              }}>
              <Broder className={classes.icon} />
            </IconButton>
            {block.checkedLink && (
              <IconButton
                aria-label="Lien actif"
                disableRipple={true}
                disableFocusRipple={true}
                className={classes.imageActionBtn}
                style={{ background: 'white', border: '1px solid #000A8C', cursor: 'auto' }}>
                <Linked className={classes.icon} style={{ fill: '#000A8C' }} />
              </IconButton>
            )}
          </div>
        </div>
      );

    case 6:
      return (
        <div className={classes.sommaire}>
          <div className={classes.sommaireTitle}>Sommaire</div>
          {allBlocks
            .filter(chapter => chapter.bloc_template === 2)
            .map(chapter => (
              <div
                key={`summary-${chapter.title_type}-${chapter.title}`}
                className={
                  chapter.title_type === 'subtitle'
                    ? classes.sommaireSubtitle
                    : classes.sommaireChapter
                }>
                {chapter.title}
              </div>
            ))}
        </div>
      );

    case 7:
      return (
        <VideoBlock
          value={{ ...block, video: block.videoBook, videoEn: block.videoBookEn }}
          onChange={({ video, videoEn, ...options }) => {
            const next = [...allBlocks];
            next[index] = {
              ...block,
              ...options,
              videoBook: video || '',
              videoBookEn: videoEn || '',
            };
            edit.syncBlocks(next);
          }}
        />
      );

    default:
      return null;
  }
};

export default BlockEditor;
